import React, { createContext, useContext, useState, useEffect } from 'react';
import { supplierAuthService } from '../services/supplierAuthService';
import { supplierModel } from '../models/supplierModel';

// Create the Supplier Auth context
const SupplierAuthContext = createContext();

// Supplier Auth context provider component
export function SupplierAuthProvider({ children }) {
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize supplier auth state
  useEffect(() => {
    const initSupplierAuth = async () => {
      try {
        // Only initialize if we're on a supplier route
        const currentPath = window.location.pathname;
        const isSupplierRoute = currentPath.startsWith('/supplier') || currentPath.startsWith('/suppliers/new');
        
        if (!isSupplierRoute) {
          console.log('Not on supplier route, skipping supplier auth initialization');
          setLoading(false);
          return;
        }
        
        const token = localStorage.getItem('supplier_token'); 
        
        // Check if token exists and is valid (not null, undefined, or "undefined" string)
        if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      
          try {
            // Fetch actual supplier data from the dashboard API
            const dashboardResponse = await supplierModel.getDashboardData(); 
            
            if (dashboardResponse && dashboardResponse.data) {
              const supplierData = dashboardResponse.data;
              
              // Use the raw supplier data as-is, just add the token
              const actualSupplier = {
                ...supplierData,
                token: token
              }; 
              setSupplier(actualSupplier);
            } else {
              console.log('No dashboard data available, using minimal supplier object');
              // If dashboard data fails, create minimal supplier object
              const minimalSupplier = {
                token: token
              };
              setSupplier(minimalSupplier);
            }
          } catch (dataError) {
            console.error('Error fetching supplier data:', dataError);
            // If fetching fails but token is valid, create minimal supplier object
            const minimalSupplier = {
              token: token
            };
            setSupplier(minimalSupplier);
          }
        } else {
          console.log('Invalid or missing token, clearing localStorage');
          // Clear invalid token
          localStorage.removeItem('supplier_token');
        }
      } catch (error) {
        console.error('Error initializing supplier auth:', error);
        localStorage.removeItem('supplier_token');
      } finally {
        setLoading(false);
      }
    };

    initSupplierAuth();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await supplierAuthService.login(credentials);
      console.log('Login response received:', response);
      
      if (response.status === 1 && response.data && response.data.token && response.data.token.trim() !== '') {
        const token = response.data.token;
        console.log('Login successful - storing token:', token);
        
        // Validate token before storing
        if (token !== 'null' && token !== 'undefined') {
          localStorage.setItem('supplier_token', token);
          
          try {
            // Fetch actual supplier data after successful login
            const dashboardResponse = await supplierModel.getDashboardData();
            console.log('Post-login dashboard response:', dashboardResponse);
            
            if (dashboardResponse && dashboardResponse.data) {
              const supplierData = dashboardResponse.data;
              
              // Use the raw supplier data as-is, just add the token
              const actualSupplier = {
                ...supplierData,
                token: token
              };
              
              console.log('Setting supplier with actual post-login data:', actualSupplier);
              setSupplier(actualSupplier);
            } else {
              // Fallback to basic data if dashboard fetch fails
              const basicSupplier = {
                token: token
              };
              setSupplier(basicSupplier);
            }
          } catch (dataError) {
            console.error('Error fetching supplier data after login:', dataError);
            // Fallback to basic data
            const basicSupplier = {
              token: token
            };
            setSupplier(basicSupplier);
          }
          
          return { success: true };
        } else {
          console.error('Invalid token received:', token);
          throw new Error('Invalid token received from server');
        }
      } else {
        console.error('Login failed or no token:', response);
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (registrationData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await supplierAuthService.register(registrationData);
      
      // If registration/OTP verification is successful and token is provided
      if (response.status === 1 && response.token) {
        console.log('Registration successful with token, setting supplier state');
        localStorage.setItem('supplier_token', response.token);
        
        // Set minimal supplier object with token
        // Dashboard data will be fetched when navigating to dashboard
        const minimalSupplier = {
          token: response.token
        };
        setSupplier(minimalSupplier);
      }
      
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Registration failed';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Reset password function
  const resetPassword = async (resetData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await supplierAuthService.resetPassword(resetData);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Password reset failed';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Setup password function (after password reset or registration)
  const setupPassword = async (passwordData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await supplierAuthService.setupPassword(passwordData);
      
      // If successful, store the authentication token and set supplier state
      if (response.status === 1 && response.token) {
        console.log('Password setup successful, setting supplier state');
        localStorage.setItem('supplier_token', response.token);
        localStorage.removeItem('supplier_needs_password_setup');
        
        // Set supplier data - user can be in response.user or response.data.user
        const userData = response.user || response.data?.user;
        if (userData) {
          const supplierData = {
            ...userData,
            token: response.token
          };
          console.log('Setting supplier with user data:', supplierData);
          setSupplier(supplierData);
        } else {
          // Set minimal supplier object if user data not available
          console.log('Setting minimal supplier object with token');
          setSupplier({ token: response.token });
        }
      }
      
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Password setup failed';
      setError(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    console.log('Logging out - clearing all supplier data');
    localStorage.removeItem('supplier_token');
    setSupplier(null);
    setError(null);
  };

  // Clear corrupted tokens function
  const clearCorruptedToken = () => {
    console.log('Clearing corrupted token data');
    localStorage.removeItem('supplier_token');
    setSupplier(null);
    setError('Authentication token was corrupted. Please log in again.');
  };

  // Check if supplier is authenticated with enhanced validation
  const isAuthenticated = () => {
    const token = localStorage.getItem('supplier_token');
    const hasValidSupplier = supplier !== null;
    const hasValidToken = token && token !== 'null' && token !== 'undefined' && token.trim() !== '';
 
    // If we have supplier state but invalid token, clear everything
    if (hasValidSupplier && !hasValidToken) { 
      clearCorruptedToken();
      return false;
    }
    
    return hasValidSupplier && hasValidToken;
  };

  // Get supplier details
  const getSupplierDetails = () => {
    return supplier;
  };

  // Refresh supplier data from API
  const refreshSupplierData = async () => {
    try {
      const token = localStorage.getItem('supplier_token');
      if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
        return;
      }

      console.log('Refreshing supplier data...');
      const dashboardResponse = await supplierModel.getDashboardData();
      
      if (dashboardResponse && dashboardResponse.data) {
        const supplierData = dashboardResponse.data;
        
        // Use the raw supplier data as-is, just add the token
        const updatedSupplier = {
          ...supplierData,
          token: token
        };
        
        console.log('Supplier data refreshed:', updatedSupplier);
        setSupplier(updatedSupplier);
        return updatedSupplier;
      }
    } catch (error) {
      console.error('Error refreshing supplier data:', error);
    }
  };

  // Update supplier state (e.g., after profile update)
  const updateSupplier = (updatedSupplier) => {
    setSupplier(updatedSupplier);
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  const value = {
    supplier,
    loading,
    error,
    login,
    register,
    resetPassword,
    setupPassword,
    logout,
    clearCorruptedToken,
    isAuthenticated,
    getSupplierDetails,
    refreshSupplierData,
    updateSupplier,
    clearError
  };

  return (
    <SupplierAuthContext.Provider value={value}>
      {children}
    </SupplierAuthContext.Provider>
  );
}

// Custom hook to use supplier auth context
export function useSupplierAuth() {
  const context = useContext(SupplierAuthContext);
  if (!context) {
    throw new Error('useSupplierAuth must be used within a SupplierAuthProvider');
  }
  return context;
}
